import React, { Component } from 'react';
import { ClockIcon, BookOpenIcon, SparklesIcon, ClipboardCheckIcon, XIcon, CheckIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
type Row = {
  icon: ComponentType<{
    className?: string;
  }>;
  category: string;
  before: string;
  after: string;
  metric: string;
};
const rows: Row[] = [{
  icon: ClockIcon,
  category: 'Pianificazione',
  before: 'Notti intere a creare schede ed esercizi da zero.',
  after: 'Unità didattiche pronte sul tuo tablet in soli 2 clic.',
  metric: '−90% tempo'
}, {
  icon: BookOpenIcon,
  category: 'Spiegazioni',
  before: 'Stress prima di spiegare concetti complessi (frazioni, logica).',
  after: 'Sicurezza totale con spiegazioni chiare, visive e mirate.',
  metric: 'Zero ansia'
}, {
  icon: SparklesIcon,
  category: 'Coinvolgimento',
  before: 'Alunni annoiati da libri di testo ripetitivi.',
  after: 'Giochi matematici e laboratori che incantano la classe.',
  metric: '+100% attenzione'
}, {
  icon: ClipboardCheckIcon,
  category: 'Valutazione',
  before: 'Ansia per le prove INVALSI e le valutazioni ministeriali.',
  after: 'Verifiche e simulazioni già pronte e allineate al curriculum.',
  metric: 'INVALSI ready'
}];
export function ContrastSection() {
  return <section className="w-full bg-bg-main py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-128">
      {/* Subtle accent glows */}
      <div className="absolute top-0 -left-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(239,68,68,0.08) 0%, transparent 60%)'
    }} aria-hidden="true" data-id="element-129" />
      <div className="absolute bottom-0 -right-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 60%)'
    }} aria-hidden="true" data-id="element-130" />

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-131">
        {/* Section header — compact */}
        <div className="text-center mb-8 sm:mb-10" data-id="element-132">
          <span className="inline-flex items-center gap-2 text-primary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full mb-3 sm:mb-4" data-id="element-133">
            ⚡ Prima e dopo
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.05] mb-3" data-id="element-134">
            Da maestra esausta
            <br className="hidden sm:block" data-id="element-135" />
            <span className="text-primary" data-id="element-136"> a maestra serena.</span>
          </h2>
          <p className="text-text-sec text-sm sm:text-lg max-w-xl mx-auto leading-relaxed" data-id="element-137">
            Recupera{' '}
            <span className="text-badge-red font-black" data-id="element-138">150+ ore l'anno</span> —
            l'equivalente di{' '}
            <span className="text-text-main font-bold" data-id="element-139">un mese intero</span> di
            lavoro.
          </p>
        </div>

        {/* Comparison table */}
        <div className="bg-bg-alt rounded-2xl sm:rounded-3xl shadow-2xl border border-border-light overflow-hidden" data-id="element-140">
          {/* Header */}
          <div className="grid grid-cols-2 border-b border-border-light" data-id="element-141">
            <div className="px-4 sm:px-8 py-3 sm:py-5 bg-badge-red/5 border-r border-border-light" data-id="element-142">
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-143">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-badge-red/15 border border-badge-red/30 flex items-center justify-center flex-shrink-0" data-id="element-144">
                  <XIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-badge-red" strokeWidth={3} data-id="element-145" />
                </div>
                <p className="text-red-400 font-black text-xs sm:text-base leading-tight" data-id="element-146">
                  Senza il Kit
                </p>
              </div>
            </div>
            <div className="px-4 sm:px-8 py-3 sm:py-5 bg-cta-green/5" data-id="element-147">
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-148">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-cta-green/15 border border-cta-green/30 flex items-center justify-center flex-shrink-0" data-id="element-149">
                  <CheckIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cta-green" strokeWidth={3} data-id="element-150" />
                </div>
                <p className="text-cta-green font-black text-xs sm:text-base leading-tight" data-id="element-151">
                  Con Matematica Pronta
                </p>
              </div>
            </div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-border-light" data-id="element-152">
            {rows.map((row, i) => {
            const Icon = row.icon;
            return <div key={i} className="grid grid-cols-2 group" data-id="element-153">
                  {/* BEFORE cell */}
                  <div className="px-3 sm:px-8 py-4 sm:py-5 flex items-start gap-2.5 sm:gap-4 border-r border-border-light bg-black/20 transition-colors group-hover:bg-black/30" data-id="element-154">
                    <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg bg-badge-red/10 border border-badge-red/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-155">
                      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-badge-red/70" strokeWidth={2} data-id="element-156" />
                    </div>
                    <p className="text-text-sec font-medium text-[13px] sm:text-base leading-snug line-through decoration-badge-red/40 decoration-1 min-w-0" data-id="element-157">
                      {row.before}
                    </p>
                  </div>

                  {/* AFTER cell */}
                  <div className="px-3 sm:px-8 py-4 sm:py-5 flex items-start gap-2.5 sm:gap-4 bg-cta-green/[0.04] transition-colors group-hover:bg-cta-green/[0.08]" data-id="element-158">
                    <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg bg-cta-green/15 border border-cta-green/30 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-[0_0_12px_rgba(16,185,129,0.15)]" data-id="element-159">
                      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cta-green" strokeWidth={2.5} data-id="element-160" />
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-161">
                      <p className="text-text-main font-bold text-[13px] sm:text-base leading-snug mb-1" data-id="element-162">
                        {row.after}
                      </p>
                      <span className="inline-flex items-center gap-1 bg-cta-green/15 text-cta-green text-[9px] sm:text-[10px] font-black px-2 py-0.5 rounded-full border border-cta-green/20" data-id="element-163">
                        <TrendingUpIcon className="w-2.5 h-2.5" strokeWidth={3} data-id="element-164" />
                        {row.metric}
                      </span>
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {/* Bottom summary bar */}
          <div className="grid grid-cols-3 gap-px bg-border-light border-t border-border-light" data-id="element-165">
            <div className="bg-bg-alt px-2 py-3 sm:py-4 text-center" data-id="element-166">
              <p className="text-cta-green font-black text-xl sm:text-3xl tracking-tight leading-none" data-id="element-167">
                +150h
              </p>
              <p className="text-text-sec text-[9px] sm:text-xs font-bold uppercase tracking-wider mt-1" data-id="element-168">
                Risparmiate
              </p>
            </div>
            <div className="bg-bg-alt px-2 py-3 sm:py-4 text-center" data-id="element-169">
              <p className="text-cta-green font-black text-xl sm:text-3xl tracking-tight leading-none" data-id="element-170">
                2 clic
              </p>
              <p className="text-text-sec text-[9px] sm:text-xs font-bold uppercase tracking-wider mt-1" data-id="element-171">
                Per lezione
              </p>
            </div>
            <div className="bg-bg-alt px-2 py-3 sm:py-4 text-center" data-id="element-172">
              <p className="text-cta-green font-black text-xl sm:text-3xl tracking-tight leading-none" data-id="element-173">
                100%
              </p>
              <p className="text-text-sec text-[9px] sm:text-xs font-bold uppercase tracking-wider mt-1" data-id="element-174">
                Curriculum
              </p>
            </div>
          </div>
        </div>

        {/* Closing line — single power statement */}
        <p className="text-center text-text-main text-base sm:text-xl font-semibold mt-6 sm:mt-8 max-w-2xl mx-auto leading-snug" data-id="element-175">
          Stesso programma.{' '}
          <span className="text-primary font-black" data-id="element-176">Metà del tempo.</span> Zero
          stress.
        </p>
      </div>
    </section>;
}